// src/pages/Campaigns.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCampaigns, createCampaign } from '../services/campaignService';
import { uploadLeads } from '../services/leadsService';

const Campaigns = () => {
    const navigate = useNavigate();

    // State for campaign list and loading indicator.
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);

    // State for Create Campaign Modal.
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newCampaign, setNewCampaign] = useState({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
    });

    // State for Leads Modal (CSV upload) for a specific campaign.
    const [isLeadsModalOpen, setIsLeadsModalOpen] = useState(false);
    const [currentCampaignForLeads, setCurrentCampaignForLeads] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);

    // Fetch campaigns from the backend.
    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const response = await getCampaigns();
            setCampaigns(response.data.campaigns);
        } catch (error) {
            console.error('Error fetching campaigns:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    // --- Create Campaign Handlers ---
    const openCreateModal = () => {
        setNewCampaign({
            name: '',
            description: '',
            startDate: '',
            endDate: '',
        });
        setIsCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        setIsCreateModalOpen(false);
    };

    const handleCreateInputChange = (e) => {
        const { name, value } = e.target;
        setNewCampaign((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreateCampaign = async () => {
        if (!newCampaign.name) {
            alert('Campaign name is required');
            return;
        }
        // Map description to configuration as expected by the backend.
        const payload = {
            name: newCampaign.name,
            configuration: newCampaign.description,
            startDate: newCampaign.startDate || null,
            endDate: newCampaign.endDate || null,
        };
        try {
            await createCampaign(payload);
            fetchCampaigns();
            closeCreateModal();
        } catch (error) {
            console.error('Error creating campaign:', error);
        }
    };

    // --- Leads Modal Handlers for CSV Upload ---
    const openLeadsModal = (campaign) => {
        setCurrentCampaignForLeads(campaign);
        setIsLeadsModalOpen(true);
    };

    const closeLeadsModal = () => {
        setIsLeadsModalOpen(false);
        setCurrentCampaignForLeads(null);
        setUploadFile(null);
    };

    const handleFileChange = (e) => {
        setUploadFile(e.target.files[0]);
    };

    const handleUploadLeads = async () => {
        if (!uploadFile) {
            alert('Please select a CSV file to upload.');
            return;
        }
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('campaignId', currentCampaignForLeads.id);
        try {
            await uploadLeads(formData);
            alert('Leads uploaded successfully.');
            closeLeadsModal();
        } catch (error) {
            console.error('Error uploading leads:', error);
            alert('Error uploading leads.');
        }
    };

    return (
        <div style={{ position: 'relative', padding: '20px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Campaigns</h2>
                <button className="create-campaign-btn" onClick={openCreateModal}>
                    Create Campaign
                </button>
            </div>

            {/* Campaign Table */}
            {loading ? (
                <p>Loading campaigns...</p>
            ) : (
                <table className="campaign-table">
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {campaigns.map((campaign) => (
                        <tr key={campaign.id}>
                            <td>{campaign.id}</td>
                            <td>{campaign.name}</td>
                            <td>{campaign.configuration}</td>
                            <td>{campaign.startDate ? new Date(campaign.startDate).toLocaleDateString() : '-'}</td>
                            <td>{campaign.endDate ? new Date(campaign.endDate).toLocaleDateString() : '-'}</td>
                            <td>
                                <button
                                    className="edit-btn"
                                    onClick={() => navigate(`/campaigns/${campaign.id}/overview`)}
                                >
                                    Overview
                                </button>
                                <button
                                    className="edit-btn"
                                    onClick={() => openLeadsModal(campaign)}
                                    style={{ marginLeft: '5px' }}
                                >
                                    Upload Leads
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            )}

            {/* Create Campaign Modal */}
            {isCreateModalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Create Campaign</h3>
                        </div>
                        <div className="modal-body">
                            <label>
                                Campaign Name*:
                                <input
                                    type="text"
                                    name="name"
                                    value={newCampaign.name}
                                    onChange={handleCreateInputChange}
                                    required
                                />
                            </label>
                            <label>
                                Campaign Description:
                                <input
                                    type="text"
                                    name="description"
                                    value={newCampaign.description}
                                    onChange={handleCreateInputChange}
                                />
                            </label>
                            <label>
                                Start Date:
                                <input
                                    type="date"
                                    name="startDate"
                                    value={newCampaign.startDate}
                                    onChange={handleCreateInputChange}
                                />
                            </label>
                            <label>
                                End Date:
                                <input
                                    type="date"
                                    name="endDate"
                                    value={newCampaign.endDate}
                                    onChange={handleCreateInputChange}
                                />
                            </label>
                        </div>
                        <div className="modal-footer">
                            <button onClick={closeCreateModal}>Cancel</button>
                            <button onClick={handleCreateCampaign}>Create</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Leads Modal for CSV Upload */}
            {isLeadsModalOpen && currentCampaignForLeads && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Upload Leads for: {currentCampaignForLeads.name}</h3>
                        </div>
                        <div className="modal-body">
                            <p>
                                Select a CSV file with columns: <strong>firstName, lastName, phoneNumber</strong>
                            </p>
                            <input type="file" accept=".csv" onChange={handleFileChange} />
                        </div>
                        <div className="modal-footer">
                            <button onClick={closeLeadsModal}>Cancel</button>
                            <button onClick={handleUploadLeads}>Upload Leads</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Campaigns;
